import { useEffect, useMemo, useState } from "react";

import { ProjectDates, ProjectDatesSchema } from "../../../../../../models";
import { GetProjectDetailsResponse, getStandardSpecificSchemaDetails } from "../../../../../../service/publicquery";
import { Status } from "../../../../../../service/Shared";
import { jsonReviver } from "../../../../../../utils";

interface DatesSectionProps {
  projectDetails: GetProjectDetailsResponse;
}

interface DatesSectionReturnData {
  projectDates?: ProjectDates;
  dateSchema?: ProjectDatesSchema;
}
export const useDatesSection = ({ projectDetails }: DatesSectionProps): DatesSectionReturnData => {
  const [dateSchema, setDateSchema] = useState<ProjectDatesSchema>();

  const projectDates = useMemo(() => {
    if (!projectDetails?.dates) return undefined;

    const parsedJsonDates = JSON.parse(projectDetails.dates, jsonReviver) as ProjectDates;

    return {
      ...parsedJsonDates,
      ongoingMonitoring: {
        ...parsedJsonDates.ongoingMonitoring,
        cycles: parsedJsonDates.ongoingMonitoring?.cycles?.sort((a, b) => a.sequence - b.sequence) ?? [],
      },
    } as ProjectDates;
  }, [projectDetails.dates]);

  // Fetches the project's standard-specific date schema so the section can gate each field on whether
  // the schema declares it (mirrors the private overview panel). The backend returns the schema as a
  // JSON string whose top-level `properties` is the per-phase shape, so we parse it and lift
  // `.properties` out. Projects without a standard-specific schema never load one and `dateSchema`
  // stays undefined, in which case the schema-gated fields simply do not render.
  //
  // The route can stay mounted while `projectDetails` switches between projects, so we clear any
  // previously loaded schema when the new project has no standard-specific schema, and ignore async
  // responses that no longer match the UUID we requested (via the effect cleanup guard).
  useEffect(() => {
    const uuid = projectDetails?.standardSpecificSchemaUuid;

    if (!uuid) {
      setDateSchema(undefined);
      return undefined;
    }

    let isCurrent = true;

    getStandardSpecificSchemaDetails({ standardSpecificSchemaUuid: uuid })
      .then((result) => {
        if (isCurrent && result.status === Status.Success && result.data?.dateSchema) {
          setDateSchema(JSON.parse(result.data.dateSchema).properties as ProjectDatesSchema);
        }
      })
      .catch(() => {
        if (isCurrent) setDateSchema(undefined);
      });

    return () => {
      isCurrent = false;
    };
  }, [projectDetails?.standardSpecificSchemaUuid]);

  return {
    projectDates,
    dateSchema,
  };
};
